import { EditOutlined } from '@ant-design/icons';
import { useLazyQuery, useQuery } from '@apollo/client/react';
import { Trans, useLingui } from '@lingui/react';
import { PosteType } from '@zeus/index';
import { Button, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { styled } from 'styled-components';
import {
  GQL_MAINTENANCES,
  StoredDevisMaintenance,
  StoredMaintenance,
} from '~/gql/devis/devis';
import { GQL_POSTES, StoredPoste } from '~/gql/poste/poste';
import { authClient } from '~/helpers/apollo';
import useDataUserStore from '~/helpers/store/data-user/data-user';
import AddMaintenance from '~/pages/components/common/maintenance/AddMaintenance';
import EditMaintenance from '~/pages/components/common/maintenance/EditMaintenance';
import EditUsersInTask from '~/pages/components/common/planning/EditUsersInTask';
import {
  SDevisTable,
  SDevisTableHead,
  SDevisTh,
  STableTr,
  STaskTd,
} from '~/pages/components/styles/styles';
import AvatarList from '~/pages/components/ui/AvatarList';
import {
  compareByDateDec,
  compareString,
  getHoursAndMinutesStrByDurationForTimes,
} from '~/utils/utils/utils';

//type IProps = {};

// GQL_CREATE_DEVIS_MAINTENANCE

function MaintenanceTable() {
  const params = useParams();
  const { id } = params;
  const { i18n } = useLingui();
  const { currentProject } = useDataUserStore();
  const [maintenance, setMaintenance] = useState<StoredMaintenance | undefined>(
    undefined
  );
  const [sortedMaintenances, setSortedMaintenances] = useState<
    StoredDevisMaintenance[]
  >([]);
  const [selectedMaintenance, setSelectedMaintenance] = useState<
    StoredDevisMaintenance | undefined
  >(undefined);
  const [openEditUsers, setOpenEditUsers] = useState<boolean>(false);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [maintenancePostes, setMaintenancePostes] = useState<StoredPoste[]>([]);
  const [sortType, setSortType] = useState<'name' | 'startDate' | 'finishDate'>(
    'name'
  );

  const { refetch: fetchMaintenances, loading } = useQuery(GQL_MAINTENANCES, {
    variables: { projectId: Number(id) },
    skip: !id,
    client: authClient,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      setMaintenance(data.getMaintenances);
      if (data.getMaintenances.maintenances) {
        sortMaintenances(data.getMaintenances.maintenances, sortType);
      }
    },

    onError: (e) => {
      console.log('erreur dans GQL_MAINTENANCES', e);
    },
  });

  const [getPostes] = useLazyQuery(GQL_POSTES, {
    client: authClient,
    onCompleted: (data) => {
      setMaintenancePostes(
        [...data.getPostes]
          .filter((el) => el.type === PosteType.MAINTENANCE)
          .sort(compareString)
      );
    },
  });

  function sortMaintenances(
    _maintenances: StoredDevisMaintenance[],
    _sortType: 'name' | 'startDate' | 'finishDate'
  ) {
    switch (_sortType) {
      case 'finishDate':
        setSortedMaintenances([
          ..._maintenances.sort((a, b) =>
            compareByDateDec(a.finishDate as Date, b.finishDate as Date)
          ),
        ]);
        break;
      case 'startDate':
        setSortedMaintenances([
          ..._maintenances.sort((a, b) =>
            compareByDateDec(a.startDate as Date, b.startDate as Date)
          ),
        ]);
        break;
      case 'name':
        setSortedMaintenances([
          ..._maintenances.sort((a, b) => {
            if (a.task?.name && b.task?.name && a.task.name < b.task.name) {
              return -1;
            }
            if (a.task?.name && b.task?.name && a.task.name > b.task.name) {
              return 1;
            }

            return 0;
          }),
        ]);
        break;
      default:
        break;
    }
  }

  useEffect(() => {
    void getPostes();
    void fetchMaintenances();
  }, []);

  function handleAddMaintenance() {
    setOpenAdd(true);
  }

  function handleEdit(m: StoredDevisMaintenance) {
    setSelectedMaintenance(m);
    setOpenEdit(true);
  }

  return (
    <Container>
      <STableContainer>
        {loading ? (
          <p>{i18n._('common.loading')} ...</p>
        ) : (
          <SDevisTable>
            <SDevisTableHead>
              <tr>
                <SDevisTh
                  $bg={sortType === 'name' ? '#bbd9f3' : '#ebebeb'}
                  $textAlign="center"
                  $minWidth="180px"
                  onClick={() => {
                    setSortType('name');
                    sortMaintenances(sortedMaintenances, 'name');
                  }}
                >
                  {i18n._('maintenance.name')}
                </SDevisTh>
                <SDevisTh
                  $textAlign="center"
                  $minWidth="180px"
                  $bg={sortType === 'startDate' ? '#bbd9f3' : '#ebebeb'}
                  onClick={() => {
                    setSortType('startDate');
                    sortMaintenances(sortedMaintenances, 'startDate');
                  }}
                >
                  {i18n._('maintenance.startDate')}
                </SDevisTh>
                <SDevisTh
                  $textAlign="center"
                  $minWidth="180px"
                  $bg={sortType === 'finishDate' ? '#bbd9f3' : '#ebebeb'}
                  onClick={() => {
                    setSortType('finishDate');
                    sortMaintenances(sortedMaintenances, 'finishDate');
                  }}
                >
                  {i18n._('maintenance.finishDate')}
                </SDevisTh>
                <SDevisTh $textAlign="center" $minWidth="180px">
                  {i18n._('common.sold')}
                </SDevisTh>
                <SDevisTh $textAlign="center" $minWidth="180px">
                  {i18n._('common.duration')}
                </SDevisTh>
                <SDevisTh $textAlign="center" $minWidth="180px">
                  {i18n._('form.edit.users')}
                </SDevisTh>
              </tr>
            </SDevisTableHead>
            <tbody>
              {sortedMaintenances.map((m, indM) => (
                <STableTr
                  key={indM}
                  onDoubleClick={() => {
                    handleEdit(m);
                  }}
                  onClick={() => {
                    handleEdit(m);
                  }}
                >
                  <STaskTd $padding="0 3px">{m.task?.name}</STaskTd>
                  <STaskTd $padding="0 3px">
                    {new Date(m.startDate as Date).toLocaleDateString()}
                  </STaskTd>
                  <STaskTd $padding="0 3px">
                    {new Date(m.finishDate as Date).toLocaleDateString()}
                  </STaskTd>
                  <STaskTd $padding="0 3px">
                    {m.amount.toFixed(2) + '€'}
                  </STaskTd>

                  <STaskTd $padding="0 3px">
                    {getHoursAndMinutesStrByDurationForTimes(m.duration)}
                  </STaskTd>
                  <STaskTd $padding="0 3px">
                    <SDiv>
                      <AvatarList
                        maxCount={10}
                        users={m.task?.users}
                      ></AvatarList>
                      <Tooltip title={i18n._('common.edit.users')}>
                        <Button
                          shape="circle"
                          onClick={() => {
                            setSelectedMaintenance(m);
                            setOpenEditUsers(true);
                          }}
                          icon={<EditOutlined />}
                        />
                      </Tooltip>
                    </SDiv>
                  </STaskTd>

                  <STaskTd $padding="0 3px">
                    <Tooltip title={i18n._('common.edit.maintenance')}>
                      <Button
                        shape="circle"
                        onClick={() => {
                          setSelectedMaintenance(m);
                          setOpenEdit(true);
                        }}
                        icon={<EditOutlined />}
                      />
                    </Tooltip>
                  </STaskTd>
                </STableTr>
              ))}
            </tbody>
          </SDevisTable>
        )}
      </STableContainer>
      <>
        <Button
          style={{ width: '300px' }}
          onClick={handleAddMaintenance}
          type="primary"
        >
          <Trans id="common.add.maintenance" />
        </Button>
      </>
      {currentProject && (
        <AddMaintenance
          postes={maintenancePostes}
          devisId={maintenance?.devisId}
          projectId={currentProject.id}
          title={i18n._('maintenance.add')}
          isOpen={openAdd}
          close={() => setOpenAdd(false)}
          confirm={() => {
            setOpenAdd(false);

            void fetchMaintenances();
          }}
        />
      )}
      {currentProject && selectedMaintenance && (
        <EditMaintenance
          maintenance={selectedMaintenance}
          postes={maintenancePostes}
          title={i18n._('maintenance.edit')}
          isOpen={openEdit}
          close={() => setOpenEdit(false)}
          confirm={() => {
            setOpenEdit(false);

            void fetchMaintenances();
          }}
        />
      )}
      {selectedMaintenance?.task?.id && (
        <EditUsersInTask
          taskId={selectedMaintenance.task.id}
          users={selectedMaintenance.task.users}
          title={i18n._('task.edit.users')}
          isOpen={openEditUsers}
          close={() => setOpenEditUsers(false)}
          confirm={() => {
            setOpenEditUsers(false);
            void fetchMaintenances();
          }}
        />
      )}
    </Container>
  );
}

export default MaintenanceTable;

const STableContainer = styled.div`
  width: max-content;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: auto;
`;
const Container = styled.div`
  overflow: hidden;
  width: 100%;
`;

const SDiv = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: space-between;
  padding: 0 5px;
  width: 100%;
`;
